/**
 * Pulizia dei dati di lavoro tenuti in localStorage all'uscita di un utente.
 *
 * In initio-studio il logout azzerava solo token, utente e fuso: tutto il resto restava nel
 * browser. Su una postazione condivisa l'utente successivo trovava — e continuava — la
 * sessione di lavoro del precedente. Nessun id da indovinare: bastava essere il secondo a
 * sedersi. Questo file esiste per non ripetere quell'errore da zero.
 *
 * Qui si cancella tutto ciò che appartiene alla PERSONA e non al BROWSER. NON si usa
 * `localStorage.clear()`: cancellerebbe anche preferenze innocue di altre app servite dalla
 * stessa origine, e soprattutto non sopravviverebbe a una revisione ("perché stiamo
 * cancellando cose che non conosciamo?").
 *
 * Quando si aggiunge una schermata che ricorda qualcosa in localStorage, la sua chiave va
 * aggiunta qui — o alla lista esatta, o ai prefissi se è generata a runtime.
 */

/** Chiavi esatte da rimuovere. */
const USER_SCOPED_KEYS = [
  // Lingua EFFETTIVA dell'utente uscito, arrivata dal server: è sua, non del browser. La
  // scelta esplicita di lingua ('app-locale') resta invece, come il tema: è una preferenza
  // della postazione, e l'utente successivo la rifarebbe identica.
  'app-locale-server',
]

/**
 * Prefissi da rimuovere: chiavi generate a runtime, una per articolo / coda / filtro.
 * Vuoto per ora — la struttura è qui perché la prima schermata con stato salvato non debba
 * anche inventarsi il posto dove dichiararlo.
 */
const USER_SCOPED_PREFIXES: string[] = []

/**
 * Da chiamare al logout, prima di dimenticare l'utente. Non tocca il token: se ne occupa lo
 * store, che è l'unico a sapere quando è il momento.
 */
export function clearUserScopedStorage(): void {
  for (const key of USER_SCOPED_KEYS) {
    localStorage.removeItem(key)
  }
  // Si raccolgono le chiavi prima di rimuoverle: `localStorage.key(i)` cambia indice sotto i
  // piedi a ogni removeItem.
  const toRemove: string[] = []
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i)
    if (key && USER_SCOPED_PREFIXES.some((prefix) => key.startsWith(prefix))) {
      toRemove.push(key)
    }
  }
  for (const key of toRemove) {
    localStorage.removeItem(key)
  }
  // La conversazione con l'assistente: sono sintomi, cioè dati sanitari di chi è uscito. Sta in
  // sessionStorage (muore con la scheda), ma un logout la deve cancellare subito.
  sessionStorage.removeItem(ASSISTANT_KEY)
  // La valutazione in corso: sintomi, date e profilo di un'altra persona. Stessa ragione.
  sessionStorage.removeItem(EVALUATION_KEY)
}

export const ASSISTANT_KEY = 'bca_assistant'
export const EVALUATION_KEY = 'bca_evaluation'
